import { useContext, useEffect, useRef } from "react";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./App.css";

import { Button } from "primereact/button";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menubar } from "primereact/menubar";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectDisplayName, selectEmail, selectIsAuthenticated } from "./services/authentication/authSlice";
import { addLocale, locale } from "primereact/api";
import { locales } from "./config";
import Footer from "./components/common/Footer";
import { Avatar } from "primereact/avatar";
import { OverlayPanel } from "primereact/overlaypanel";
import GlobalAutocomplete from "./components/common/GlobalAutocomplete";
import { getTheme, setTheme } from "./services/base/layoutSlice";
import { InputSwitch } from "primereact/inputswitch";

function App() {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const dispatch = useDispatch();
  const theme = useSelector(getTheme);

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userEmail = useSelector(selectEmail);
  const userDisplayName = useSelector(selectDisplayName);
  const op = useRef<OverlayPanel>(null);

  useEffect(() => {
    // Always keep track of the last page visited that wasn't the login page.
    if (pathname !== "/login") {
      const fullPath = `${pathname}${search}${hash}`;
      sessionStorage.setItem("redirectPath", fullPath);
    }

    // If the user is not authenticated and not on the login page, send them there.
    if (!isAuthenticated && pathname !== "/login") {
      navigate("/login");
    }
  }, [isAuthenticated, pathname, search, hash, navigate]);

  addLocale("bg", locales.bg);

  useEffect(() => {
    const layoutThemeElement = document.getElementById("layout-theme");
    if (layoutThemeElement) {
      layoutThemeElement.setAttribute("href", `/themes/${theme}/theme.css`);
      document.body.className = theme === "arya-blue" ? "layout-arya-blue" : "layout-stark-blue";
    }
  }, [theme]);

  const handleThemeSwitch = () => {
    const newTheme = theme === "arya-blue" ? "stark-blue" : "arya-blue";
    dispatch(setTheme(newTheme));
  };

  const menuItems = [
    {
      label: "Начало",
      icon: "pi pi-fw pi-home",
      command: () => {
        navigate("/dashboard");
      },
      //className: pathname === "/" ? "p-focus" : "",
    },

    {
      label: "Студенти",
      icon: "pi pi-fw pi-users",
      className: pathname.startsWith("/students") ? "p-focus border-bottom-2 border-white" : "",
      command: () => {
        navigate("/students");
      },
    },
    {
      label: "Учебен процес",
      icon: "pi pi-fw pi-book",
      className: pathname.startsWith("/plans") ? "p-focus border-bottom-2 border-white" : "",
      command: () => {
        navigate("/plans");
      },
    },
    {
      label: "Протоколи",
      icon: "pi pi-fw pi-file",
      className: pathname.startsWith("/protocols") ? "p-focus border-bottom-2 border-white" : "",
      command: () => {
        navigate("/protocols");
      },
    },
    {
      label: "Преподаватели",
      icon: "pi pi-fw pi-user",
      className: pathname.startsWith("/teachers") ? "p-focus border-bottom-2 border-white" : "",
      command: () => {
        navigate("/teachers");
      },
    },
    {
      label: "Учебно разписание",
      icon: "pi pi-fw pi-calendar-clock",
      className: pathname.startsWith("/protocols") ? "p-focus border-bottom-2 border-white" : "",
      command: () => {
        navigate("/schedule");
      },
    },
    {
      label: "Номенклатури",
      icon: "pi pi-fw pi-objects-column",
      command: () => {
        navigate("/nomenclatures");
      },
    },
    {
      label: "Помощ",
      icon: "pi pi-fw pi-question-circle",
      className: pathname.startsWith("/help") ? "p-focus border-bottom-2 border-white" : "",
      command: () => {
        navigate("/help");
      },
    },
  ];

  const menuEnd = (
    <div className="flex align-items-center gap-2 ml-8">
      <GlobalAutocomplete inputClassName="w-24rem" />
      <Avatar icon="pi pi-user" shape="circle" size="large" onClick={(e) => op.current!.toggle(e)} />
      <OverlayPanel ref={op} dismissable>
        <div>
          <h3 className="mt-0">{userDisplayName}</h3>
          <p>{userEmail}</p>
          <div className="flex align-items-center">
            <span className="mr-2">{theme === "arya-blue" ? "Тъмен режим" : "Светъл режим"}</span>
            <InputSwitch checked={theme !== "arya-blue"} onChange={handleThemeSwitch} />
          </div>
        </div>
      </OverlayPanel>
      <Button
        severity="secondary"
        icon="pi pi-sign-out"
        tooltip="Изход"
        tooltipOptions={{ position: "top" }}
        onClick={() => dispatch(logout())}
      />
    </div>
  );

  const leftContents = (
    <div className="w-full flex flex-row">
      <img src="/tru.png" alt="logo" className="w-10rem mx-2" />
      <div className="flex-grow-1 flex flex-column">
        <img src="/trakia.png" alt="ТРАКИЙСКИ УНИВЕРСИТЕТ" style={{ width: 397, height: 31 }} />
        <h2 className="font-normal text-3xl mb-3 mt-0 py-0 uppercase text-white">Студентска информационна система</h2>
        <Menubar
          model={menuItems}
          end={menuEnd}
          className="shadow-4 w-full bg-tru-light"
          pt={{
            menuitem: {
              className: "font-bold text-lg",
            },
            label: {
              className: "text-white",
            },
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-column">
      <div className="bg-tru-dark border-0 shadow-4 w-full py-4 px-6">{leftContents}</div>
      <Outlet />
      <Footer />
    </div>
  );
}

export default App;
